import React, { useState, useCallback, useEffect } from 'react';
import { Box } from "@mui/system";
import { Dialog, Loading } from "components";
import { useNotify, useScorecardConfig, useCreateScorecardRow, useUpdateScorecardRow, useSelectedSite } from 'hooks';
import { AddNewRowModalProps } from './types';
import { useFormState, useFormValidation } from './hooks';
import { FormFields, ActionButtons } from './components';
import { LAYOUT_STYLES, TEXT_STYLES } from './styles';
import { mapFormStateToSimplifiedApiRequest, mapRowDataToFormState } from './helpers';

export const AddNewRowModal: React.FC<AddNewRowModalProps> = ({
    isOpen,
    onClose,
    rowData = null,
    isEditMode = false,
    onSave
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isLastStep, setIsLastStep] = useState(false);

    const { formState, updateField, resetForm, setFormState } = useFormState();
    const isFormValid = useFormValidation(formState, isLastStep);

    const { data: configData, isLoading: isLoadingConfig, error: configError } = useScorecardConfig();
    const createRowMutation = useCreateScorecardRow();
    const updateRowMutation = useUpdateScorecardRow();
    const notify = useNotify();

    // Get selected site from localStorage
    const selectedSite = useSelectedSite();
    const siteId = selectedSite?.id;

    // Load row data when in edit mode
    useEffect(() => {
        if (isEditMode && rowData) {
            const initialFormState = mapRowDataToFormState(rowData);
            setFormState(initialFormState);
            setIsLastStep(true); // In edit mode, we start at step 2
        }
    }, [isEditMode, rowData, setFormState]);

    const handleNext = useCallback(() => {
        setIsLastStep(true);
    }, []);

    const handleBack = useCallback(() => {
        setIsLastStep(false);
        if (!isEditMode) {
            resetForm();
        }
    }, [resetForm, isEditMode]);

    const handleCancel = useCallback(() => {
        resetForm();
        setIsLastStep(false);
        onClose();
    }, [resetForm, onClose]);

    const handleSave = useCallback(async () => {
        if (!configData) {
            notify.error('Configuration data not available. Please try again.');
            return;
        }

        if (!siteId) {
            notify.error('No site selected. Please select a site first.');
            return;
        }

        setIsSaving(true);

        try {
            const apiRequest = mapFormStateToSimplifiedApiRequest(formState, siteId);

            if (isEditMode && rowData?.rowId) {
                await updateRowMutation.mutateAsync({ id: rowData.rowId, data: apiRequest });
                notify.success('Row updated successfully!');
                if (onSave) {
                    onSave(rowData.rowId, apiRequest);
                }
            } else {
                await createRowMutation.mutateAsync(apiRequest);
                notify.success('New row created successfully!');
            }

            resetForm();
            setIsLastStep(false);
            onClose();
        } catch (error) {
            notify.error(error instanceof Error ? error.message : 'Failed to save row. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }, [formState, configData, siteId, createRowMutation, updateRowMutation, notify, resetForm, onClose, isEditMode, rowData, onSave]);

    useEffect(() => {
        if (configError) {
            notify.error('Failed to load configuration data');
        }
    }, [configError, notify]);

    const isLoading = isLoadingConfig;

    return (
        <Dialog
            isOpen={isOpen}
            title={<div style={TEXT_STYLES.stepTitle}>Step {isLastStep ? '2/2' : '1/2'}</div>}
            dividers
            onClose={handleCancel}
        >
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '176px' }}><Loading /></div>
            ) : (
                <Box sx={LAYOUT_STYLES.container}>
                    <Box sx={LAYOUT_STYLES.formContainer}>
                        <div style={TEXT_STYLES.title}>{isEditMode ? 'Edit Row' : 'Add New Row'}</div>
                        <FormFields
                            formState={formState}
                            updateField={updateField}
                            dropdownsData={configData}
                            isLastStep={isLastStep}
                            rowData={rowData}
                            isEditMode={isEditMode}
                        />
                    </Box>
                    <ActionButtons
                        isLastStep={isLastStep}
                        isSaving={isSaving}
                        isFormValid={isFormValid}
                        onBack={handleBack}
                        onCancel={handleCancel}
                        onNext={handleNext}
                        onSave={handleSave}
                    />
                </Box>
            )}
        </Dialog>
    );
};
